
import * as moment from 'moment';
import 'swiper/dist/css/swiper.css';
import { swiper, swiperSlide } from 'vue-awesome-swiper';
import { Component, Vue, Watch } from 'vue-property-decorator';
import { Route } from 'vue-router';
import { State } from 'vuex-class';
import { BarometerTab, TabType } from '../../components/barometer/BarometerModel';
import Snapshot from '../../components/barometer/Snapshot.vue';
import UpdateBarometerButton from '../../components/barometer/UpdateBarometerButton.vue';
import JoeyChatbot from '../../components/chatbot/JoeyChatbot.vue';
import CoachesAndTribeInfo, { ICoachesAndTribeTooltips } from '../../components/shared/CoachesAndTribeInfo.vue';
import { AnswerType, IAnswerDatum } from '../../shared/contracts/AnswerContract';
import { ISnapshotHypermediaRepresentation } from '../../shared/contracts/SnapshotContract';
import { eventService } from '../../shared/services/EventService';
import { performance } from '../../shared/services/PerformanceService';
import { IBarometerState, IUserState } from '../../store/contract';
import store from '../../store/store';

const barometerNamespace = 'barometer';
const userNamespace = 'user';

interface IAnswerDatumWithAccount extends IAnswerDatum {
    accountId?: string;
}

@Component({
    components: {
        JoeyChatbot,
        UpdateBarometerButton,
        Snapshot,
        CoachesAndTribeInfo,
        swiper,
        swiperSlide,
    },
})
export default class Barometer extends Vue {
    @State(barometerNamespace) barometerState: IBarometerState;
    @State(userNamespace) userState: IUserState;

    private tabs: BarometerTab[] = [];
    private selectedTabIndex: number = 0;

    snapshotTooltips: ICoachesAndTribeTooltips = {
        emFullNameTooltip: 'Unknown EM',
        ltCoachFullNameTooltip: 'Unknown LTC',
        tribeNameTooltip: 'Unknown Tribe',
        firestaerterTooltip: 'Unknown Firestaerter'
    };

    get swiperOptions() {
        return {
            autoHeight: true,
            initialSlide: this.snapshotsToShow.length - 1,
            iOSEdgeSwipeDetection: true,
            pagination: {
                el: '.swiper-pagination',
                dynamicBullets: true,
            },
            navigation: {
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
            },
            keyboard: {
                enable: true,
            },
        };
    }

    @Watch('snapshotsToShow')
    goToLatestSnapshot(): void {
        // Next tick here in case the page refreshes (when searching in the searchbar).
        // In this case, "snapshotSwiper" does not exist yet as a ref in Vue until the nextTick.
        Vue.nextTick(() => {
            (this.$refs.snapshotSwiper as any).swiper.slideTo(this.snapshotsToShow.length - 1);
        });
    }

    get snapshotsToShow() {
        if (
            !this.barometerState.currentBarometer.snapshots ||
            this.barometerState.currentBarometer.snapshots.length === 0
        ) {
            return [];
        }
        return this.barometerState.currentBarometer.snapshots;
    }

    async beforeRouteEnter(to: Route, from: Route, next: Function) {
        performance.track(`Barometer ${to.params.personId}`);
        await store.dispatch('barometer/fetchBarometer', to.params.personId);
        next();
    }

    async beforeRouteUpdate(to: Route, from: Route, next: Function) {
        await store.dispatch('barometer/fetchBarometer', to.params.personId);
        next();
    }

    public track() {
        eventService.track('Navigation', 'Click Create First Barometer');
    }

    public updateHeight() {
        (this.$refs.snapshotSwiper as any).swiper.updateAutoHeight(200);
    }

    selectSnapshot(snapshotId: number) {
        const newIndex = this.snapshotsToShow.findIndex((s) => s.id === snapshotId);
        (this.$refs.snapshotSwiper as any).swiper.slideTo(newIndex);
    }

    created() {
        this.tabs.push(new BarometerTab(TabType.Snapshot, 'Answers'));
        this.tabs.push(new BarometerTab(TabType.Evolution, 'Evolution'));
        this.tabs.push(new BarometerTab(TabType.Info, 'Info'));
    }

    mounted() {
        this.$nextTick(() => {
            let measurementName = 'My barometer';
            if (!this.isOwnBarometer) {
                measurementName = 'Other Barometer';
            }

            performance.end(
                `Barometer ${
                    this.barometerState.currentBarometer.participant
                        ? this.barometerState.currentBarometer.participant.id
                        : ''
                }`,
                measurementName
            );

            if (this.isOwnBarometer) {
                eventService.pageview('/barometer/my');
            } else {
                eventService.pageview('/barometer/other');
            }
        });
    }

    get networkEvolution(): IAnswerDatum[] {
        return this.answersForType(this.barometerState.currentBarometer.snapshots, AnswerType.Network);
    }

    private answersForType(
        snapshots: ISnapshotHypermediaRepresentation[],
        type: AnswerType
    ): IAnswerDatumWithAccount[] {
        let result: IAnswerDatumWithAccount[] = [];
        snapshots.forEach((s) => {
            s.answers.forEach((a) => {
                if (a.answerType === type) {
                    let o: IAnswerDatumWithAccount = { d: s.date, v: a.quantitative, snapshotId: s.id };
                    if (a.answerType === AnswerType.Project) {
                        o.accountId = a.accountId;
                    } else if (a.answerType === AnswerType.Rotation) {
                        o.accountId = a.accountId;
                    }
                    result.push(o);
                }
            });
        });
        return result;
    }

    get projectEvolution(): { [key: string]: IAnswerDatum[] } {
        let result: { [key: string]: IAnswerDatum[] } = {};
        this.answersForType(this.barometerState.currentBarometer.snapshots, AnswerType.Project).forEach((a) => {
            if (!result[a.accountId]) {
                result[a.accountId] = [];
            }
            result[a.accountId].push({ d: a.d, v: a.v, snapshotId: a.snapshotId });
        });
        return result;
    }

    get rotationEvolution(): { [key: string]: IAnswerDatum[] } {
        let result: { [key: string]: IAnswerDatum[] } = {};
        this.answersForType(this.barometerState.currentBarometer.snapshots, AnswerType.Rotation).forEach((a) => {
            if (!result[a.accountId]) {
                result[a.accountId] = [];
            }
            result[a.accountId].push({ d: a.d, v: a.v, snapshotId: a.snapshotId });
        });
        return result;
    }

    get growthEvolution(): IAnswerDatum[] {
        return this.answersForType(this.barometerState.currentBarometer.snapshots, AnswerType.Growth);
    }

    get participantName(): string {
        return this.barometerState.currentBarometer.participant.fullName;
    }

    get headerText(): string {
        if (this.isOwnBarometer) {
            return 'My barometer';
        } else {
            let suffix = "'";
            if (this.participantName.substring(this.participantName.length - 1) !== 's') {
                suffix += 's';
            }
            return `${this.participantName}${suffix} barometer`;
        }
    }

    get isOwnBarometer(): boolean {
        return (
            this.barometerState.currentBarometer.participant &&
            this.barometerState.currentBarometer.participant.id === this.userState.user.id
        );
    }

    get snapshotContentClass() {
        return `barometer__content__snapshot-content--${this.tabs[this.selectedTabIndex].tabType.toLowerCase()}`;
    }
}
